/*
 * SoMatcha charm add-on popup for product cards.
 *
 * Pressing a card's Add to Cart opens this dialog instead of adding immediately. The
 * customer either ticks the charm and confirms (product + charm, one request), confirms
 * without ticking (product only), or dismisses (nothing added).
 *
 * REGRESSION SAFETY: this element is rendered by snippets/card-product.liquid ONLY when
 * custom.addon_product resolves to an available product. A product without the metafield
 * emits no element, so this script never loads, no listener is ever attached, and the card
 * uses precisely the code path that shipped before. That property is structural, not
 * something enforced here at runtime.
 *
 * Relationship to assets/sm-tumbler-addon.js: deliberately separate, with the ~40 lines of
 * items[] helper duplicated rather than shared. The two interception modes are opposites —
 * the product-page block NEVER cancels the submit, this one always cancels the first pass —
 * and refactoring the shipped page script to serve both would put the product page's
 * purchase path at risk for the sake of a few lines.
 *
 * Extends the theme's ModalDialog (assets/global.js:602) rather than reimplementing a
 * dialog: focus trap, Escape, backdrop click, focus return to the opener and body scroll
 * lock all come from there. global.js is NOT modified.
 */
if (typeof ModalDialog !== 'undefined' && !customElements.get('sm-charm-popup')) {
  class SmCharmPopup extends ModalDialog {
    constructor() {
      // ModalDialog's constructor calls addEventListener on [id^="ModalClose-"] with no
      // null guard, so the markup must always contain that button. It does.
      super();

      /*
       * Resolve the owning card HERE, in the constructor, because ModalDialog's
       * connectedCallback relocates this element to document.body — after which
       * closest('.sm-card') would return null.
       *
       * Scoped to the card, never to the section: a grid renders one <product-form> per
       * card, so closest('.shopify-section').querySelector('product-form') would resolve
       * to the FIRST card's form for every card on the page.
       */
      this.card = this.closest('.sm-card');
      this.productForm = this.card ? this.card.querySelector('product-form') : null;
      this.form = this.productForm ? this.productForm.querySelector('form') : null;

      this.confirmed = false;
      this.pendingAddon = false;
      this.inFlight = false;
      this.retried = false;
      // Double-add guard — see hide(). Starts false so the very first dismissal, before
      // any button is pressed, still adds the product.
      this.submissionStarted = false;
      this.injected = [];
    }

    connectedCallback() {
      // Relocates to document.body on the first call and re-enters this method; the base
      // class guards with its own `moved` flag and our `initialised` flag keeps the
      // wiring below to exactly one run.
      super.connectedCallback();
      if (this.initialised) return;
      this.initialised = true;
      if (!this.productForm || !this.form) return;

      this.addWithCharmButton = this.querySelector('.sm-charm-popup__add');
      this.addOnlyButton = this.querySelector('.sm-charm-popup__add-only');
      this.messageEl = this.querySelector('.sm-charm-popup__message');
      this.submitButton = this.productForm.querySelector('[type="submit"]');
      this.charmVariantId = this.dataset.charmVariantId;
      this.msgUnavailable = this.dataset.msgUnavailable || '';

      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.productForm.addEventListener('submit', this.onSubmitCapture, true);
      if (this.addWithCharmButton) this.addWithCharmButton.addEventListener('click', this.onAddWithCharm.bind(this));
      if (this.addOnlyButton) this.addOnlyButton.addEventListener('click', this.onAddProductOnly.bind(this));

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this));
        this.unsubscribeUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.productForm) this.productForm.removeEventListener('submit', this.onSubmitCapture, true);
      if (this.unsubscribeError) this.unsubscribeError();
      if (this.unsubscribeUpdate) this.unsubscribeUpdate();
      this.restoreForm();
    }

    /* Capture phase on <product-form>, ahead of product-form.js's own listener. */
    onSubmitCapture(event) {
      // Second pass: we dispatched this ourselves, so let the theme handle it untouched.
      if (this.confirmed) return;
      // First pass: cancel outright and offer the choice.
      event.preventDefault();
      event.stopPropagation();
      this.retried = false;
      // Fresh interaction: nothing has been submitted for this press yet, so a dismissal
      // from here must still add the product. Cleared here and only here.
      this.submissionStarted = false;
      this.hideMessage();
      // ModalDialog.show records the opener, so hide() returns focus to it.
      this.show(this.submitButton);
    }

    /*
     * Exit 1 — the charm row's action: add the product AND the charm, in one request.
     */
    onAddWithCharm() {
      if (!this.form) return;
      this.hideMessage();
      if (this.applyItems()) {
        this.pendingAddon = true;
        // FormData is built synchronously by product-form.js in the handler that runs
        // right after ours, so restoring on the next macrotask is safe.
        setTimeout(() => this.restoreForm(), 0);
      }
      // The dialog deliberately stays OPEN until the add succeeds. If the charm turns out
      // to be unavailable we need somewhere visible to say so, and a message written into
      // an already-closed dialog would never be read.
      this.submitConfirmed();
    }

    /*
     * Exit 2 — add the product WITHOUT the charm. No items[] rewrite at all, so the form
     * posts exactly what the theme would have posted on its own. This is the fix for the
     * old defect where dismissing the dialog silently added nothing: the customer's
     * original intent now has an explicit, labelled path.
     */
    onAddProductOnly() {
      if (!this.form) return;
      this.hideMessage();
      this.restoreForm();
      this.pendingAddon = false;
      this.submitConfirmed();
    }

    /*
     * The confirmed flag is true for EXACTLY one synchronous dispatch: set immediately
     * before requestSubmit and cleared in a finally immediately after it returns.
     * requestSubmit dispatches the submit event synchronously, so the capture handler
     * observes the flag as true exactly once, then it is false again. Since the handler
     * only opens the dialog when the flag is false, and only ever clears it here, a
     * re-entrant loop is impossible even if requestSubmit throws.
     */
    submitConfirmed() {
      if (typeof this.form.requestSubmit !== 'function') return;
      // The single place any submission is dispatched, so this is the single place the
      // double-add guard is set. Set BEFORE dispatching, so a dismissal that races the
      // request can never trigger a second add.
      this.submissionStarted = true;
      this.inFlight = true;
      this.confirmed = true;
      try {
        this.form.requestSubmit();
      } finally {
        this.confirmed = false;
      }
    }

    /*
     * Exits 3, 4 and 5 — the close button, Escape and a backdrop click.
     *
     * This dialog is an upsell AFTER the purchase decision, not a gate before it. The
     * customer already pressed Add to Cart, so their intent to buy the product is
     * established and the dialog only asks whether the charm comes too. Dismissing it
     * therefore DECLINES THE CHARM — it never cancels the purchase. All three dismissal
     * routes are funnelled here by ModalDialog (which is not modified), so overriding this
     * one method covers all of them.
     *
     * DOUBLE-ADD GUARD: this.submissionStarted.
     *   set     - in submitConfirmed(), the single place any submission is dispatched
     *   cleared - in onSubmitCapture(), when a fresh press of Add to Cart opens the dialog
     * If it is false, nothing has been submitted yet, so a dismissal adds the product and
     * defers closing to the success handler. If it is true, the product is already on its
     * way (or already failed), so we simply close. A second add is therefore unreachable:
     * every add path goes through submitConfirmed, and submitConfirmed always sets the flag
     * before dispatching.
     */
    hide() {
      // If we could not actually dispatch an add, fall through and close rather than
      // return early — otherwise a dismissal would neither add nor close, trapping the
      // customer in an un-closable dialog.
      const canSubmit = this.form && typeof this.form.requestSubmit === 'function';
      if (!this.submissionStarted && canSubmit) {
        this.onAddProductOnly();
        // Deliberately not closing yet — onCartUpdate closes on success, which keeps the
        // dialog available for the sold-out-charm message.
        return;
      }
      super.hide();
      this.restoreForm();
      this.pendingAddon = false;
      this.retried = false;
    }

    /* --- items[] helpers (duplicated from sm-tumbler-addon.js on purpose, see header) --- */

    field(name) {
      if (!this.form || !this.form.elements) return null;
      const found = this.form.elements.namedItem(name);
      if (!found) return null;
      return typeof found.length === 'number' && !('value' in found) ? found[0] : found;
    }

    applyItems() {
      const idInput = this.field('id');
      if (!idInput || !idInput.value) return false;

      // Cards carry no quantity control, so the product adds at 1 and the charm matches it.
      const qtyInput = this.field('quantity');
      const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      // Disabled, never removed: product-form.js's constructor sets
      // this.variantIdInput.disabled = false on [name=id] and would throw without it.
      idInput.disabled = true;
      if (qtyInput) qtyInput.disabled = true;

      this.injected = [
        this.hiddenInput('items[0][id]', idInput.value),
        this.hiddenInput('items[0][quantity]', String(quantity)),
        this.hiddenInput('items[1][id]', this.charmVariantId),
        this.hiddenInput('items[1][quantity]', String(quantity)),
      ];
      this.injected.forEach((input) => this.form.appendChild(input));
      return true;
    }

    hiddenInput(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.dataset.smCharm = 'true';
      return input;
    }

    restoreForm() {
      if (!this.form) return;
      this.form.querySelectorAll('[data-sm-charm]').forEach((input) => input.remove());
      const idInput = this.field('id');
      const qtyInput = this.field('quantity');
      if (idInput) idInput.disabled = false;
      if (qtyInput) qtyInput.disabled = false;
      this.injected = [];
    }

    /* --- outcome handling --- */

    onCartUpdate() {
      if (!this.inFlight) return;
      this.inFlight = false;
      this.pendingAddon = false;
      this.retried = false;
      this.restoreForm();
      if (this.hasAttribute('open')) this.hide();
    }

    onCartError(data) {
      // Ignore anything that is not the submission we dispatched.
      if (!this.inFlight) return;
      if (data && data.source && data.source !== 'product-form') return;

      if (!this.pendingAddon) {
        // No charm was involved, so this is the theme's ordinary failure. Close and let
        // its existing error surface on the card's own button.
        this.inFlight = false;
        if (this.hasAttribute('open')) this.hide();
        return;
      }

      this.pendingAddon = false;
      if (this.retried) {
        // One retry only. Stop here and leave the error visible rather than looping.
        this.inFlight = false;
        return;
      }
      this.retried = true;

      // The request was atomic, so nothing was added. The charm is the only line we added
      // to an otherwise-valid submission: drop it, say so, and re-send the product alone.
      this.showMessage(this.msgUnavailable);

      setTimeout(() => {
        // Deferred so product-form.js finishes its error handling and its finally block
        // clears the loading state before we submit again.
        this.restoreForm();
        this.submitConfirmed();
      }, 0);
    }

    showMessage(text) {
      if (!this.messageEl || !text) return;
      this.messageEl.textContent = text;
      this.messageEl.removeAttribute('hidden');
    }

    hideMessage() {
      if (!this.messageEl) return;
      this.messageEl.textContent = '';
      this.messageEl.setAttribute('hidden', '');
    }
  }

  customElements.define('sm-charm-popup', SmCharmPopup);
}
